import { useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Snackbar, Alert as MuiAlert, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { faSave, faUndo } from "@fortawesome/free-solid-svg-icons";
import { useDarkMode } from "../../contexts/ThemeContext";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import { usePermissionGuard } from "../../hooks/usePermissionGuard";
import { useDonateMaterialsData } from "../../hooks/useDonateMaterialsData";
import DetailedViewPanel from "../../components/DonateMaterials/DetailedViewPanel";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import ActionButton from "../../components/ui/ActionButton";
import routing from "../../routing";

function Workspace() {
  const { t } = useTranslation();
  const { darkMode } = useDarkMode();
  const navigate = useNavigate();
  const { workspaceListing, setWorkspaceListing, workspaceHasChanges, setWorkspaceHasChanges, clearWorkspace } = useWorkspace();
  const panelRef = useRef(null);
  const [saveVersion, setSaveVersion] = useState(0);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const { isChecking, hasPermission } = usePermissionGuard("donor", "donate_materials");
  const { listingsData, handleSaveChanges } = useDonateMaterialsData(null);

  const handleClose = useCallback(() => {
    clearWorkspace();
    navigate(routing.donor.donate_materials.path);
  }, [clearWorkspace, navigate]);

  const handleSave = useCallback(async (updatedRow) => {
    setWorkspaceListing(updatedRow);
    setWorkspaceHasChanges(false);
    const result = await handleSaveChanges(null, updatedRow);
    if (result) {
      setSaveVersion((v) => v + 1);
      setSnackbar({ open: true, message: result.message, severity: result.success ? "success" : "error" });
    }
  }, [handleSaveChanges, setWorkspaceListing, setWorkspaceHasChanges]);

  const handleSaveClick = useCallback(async () => {
    if (!panelRef.current) return;
    setSaving(true);
    await panelRef.current.flushPendingImages?.();
    const updatedRow = panelRef.current.getUpdatedRow();
    await handleSave(updatedRow);
    setSaving(false);
  }, [handleSave]);

  const handleCancelClick = useCallback(() => {
    if (panelRef.current) {
      panelRef.current.resetChanges();
      panelRef.current.clearPendingImages?.();
    }
    setWorkspaceHasChanges(false);
  }, [setWorkspaceHasChanges]);

  const handleUnsavedChanges = useCallback((hasChanges) => {
    setWorkspaceHasChanges(hasChanges);
  }, [setWorkspaceHasChanges]);

  if (isChecking) {
    return <LoadingSpinner fullPage size={50} message={t("Checking permissions...")} />;
  }

  if (!hasPermission) return null;

  if (!workspaceListing) {
    navigate(routing.donor.donate_materials.path, { replace: true });
    return null;
  }

  return (
    <div style={{ padding: "24px 32px" }}>
      <style>{`
        .workspace-page .detailed-view-panel,
        .workspace-page .detailed-view-panel.card,
        .workspace-page .detailed-view-panel > .card__content {
          animation: none !important;
          background: transparent !important;
          border: none !important;
          box-shadow: none !important;
          border-radius: 0 !important;
          overflow: visible !important;
          padding: 0 !important;
        }
        .workspace-page .detailed-view-panel::before {
          display: none !important;
        }
        .workspace-page .info-card {
          background: var(--bg-paper) !important;
          border-color: rgba(255,255,255,0.06) !important;
        }
        .workspace-page .panel-header {
          display: none !important;
        }
        .workspace-page .panel-content-grid {
          margin-top: 0 !important;
        }
      `}</style>

      {/* standard page title row */}
      <div className="workspace-page">
        <DetailedViewPanel
          ref={panelRef}
          key={workspaceListing.id}
          row={workspaceListing}
          darkMode={darkMode}
          onClose={handleClose}
          onSave={handleSave}
          listingsData={listingsData}
          saveVersion={saveVersion}
          onUnsavedChangesChange={handleUnsavedChanges}
        />
      </div>

      {/* sticky save/cancel bar */}
      {workspaceHasChanges && (
        <Box sx={{
          position: "fixed",
          bottom: 70,
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          py: 1,
          borderRadius: "14px",
          backgroundColor: darkMode ? "#1e1e1e" : "#fff",
          border: "1px solid rgba(255,255,255,0.08)",
          boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
          zIndex: 1100,
        }}>
          <Box sx={{ fontFamily: "Poppins, sans-serif", fontSize: "0.82rem", color: "#666", px: 0.5, whiteSpace: "nowrap" }}>
            {t("Unsaved changes")}
          </Box>
          <ActionButton
            onClick={handleCancelClick}
            variant="default"
            disabled={saving}
          >
            {t("Discard")}
          </ActionButton>
          <ActionButton
            icon={faSave}
            onClick={handleSaveClick}
            variant="secondary"
            loading={saving}
            disabled={saving}
          >
            {saving ? t("Saving...") : t("Save Changes")}
          </ActionButton>
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <MuiAlert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {t(snackbar.message)}
        </MuiAlert>
      </Snackbar>
    </div>
  );
}

export default Workspace;
